import React, { useEffect, useRef, useState } from 'react';
// Api
import client from '../../api/client';
// Constants
import { RESET_PASSWORD_REQUEST_API } from '../../utils/ApiRoutes';
// Components
import LoadingSpinner from '../utils/LoadingSpinner';

function ForgotPasswordForm() {
  const [resetRequestInProgress, setResetRequestInProgress] = useState(false);
  const [resetRequestError, setResetRequestError] = useState(null);
  const [resetRequestSuccess, setResetRequestSuccess] = useState(null);
  const [resetFormData, setResetFormData] = useState({ email: '' });

  const emailRef = useRef(null);
  const alertRef = useRef(null);

  useEffect(() => {
    // Focus meaningful target on result
    if (resetRequestError && alertRef.current) {
      alertRef.current.focus();
    } else if (resetRequestSuccess && alertRef.current) {
      alertRef.current.focus();
    }
  }, [resetRequestError, resetRequestSuccess]);

  const handleResetRequest = (event) => {
    event.preventDefault();
    setResetRequestInProgress(true);
    setResetRequestError(null);
    setResetRequestSuccess(null);

    client
      .post(RESET_PASSWORD_REQUEST_API, resetFormData, false)
      .then(() => {
        setResetRequestInProgress(false);
        setResetRequestSuccess(
          'If an account exists for that email, a reset link has been sent.'
        );
      })
      .catch((err) => {
        const apiMsg =
          err?.response?.data?.data?.email ||
          err?.response?.data?.email ||
          err?.response?.data?.message ||
          err?.message ||
          'Unable to request password reset. Please try again.';
        setResetRequestError(apiMsg);
        setResetRequestInProgress(false);
      });
  };

  const handleChange = (event) => {
    setResetRequestError(null);
    setResetRequestSuccess(null);
    const { name, value } = event.target;
    setResetFormData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <form
      className='grid gap-y-4'
      onSubmit={handleResetRequest}
      aria-describedby='forgot-help'
      aria-busy={resetRequestInProgress ? 'true' : 'false'}
      noValidate
    >
      <p id='forgot-help' className='sr-only'>
        Enter the email associated with your account to receive a reset link.
      </p>

      {/* Email */}
      <div className='grid gap-y-2'>
        <label
          htmlFor='email'
          className='block text-center text-sm font-medium text-colour8 dark:text-colour3'
        >
          Email
        </label>
        <input
          ref={emailRef}
          type='email'
          name='email'
          id='email'
          className='bg-colour7 border-solid border-colour3 border-2 w-full rounded-md py-2 px-2'
          placeholder='name@email.com'
          onChange={handleChange}
          value={resetFormData.email}
          required
          inputMode='email'
          autoComplete='email'
          autoCapitalize='none'
          spellCheck='false'
          aria-invalid={resetRequestError ? 'true' : 'false'}
          aria-describedby={resetRequestError ? 'email-error' : 'email-hint'}
        />
        <span id='email-hint' className='sr-only'>
          Enter the email you used to sign up.
        </span>
        <span id='email-error' className='sr-only'>
          {resetRequestError ? String(resetRequestError) : ''}
        </span>
      </div>

      {/* Submit */}
      <div className='grid h-fit w-full'>
        <button
          type='submit'
          className='px-4 py-2 rounded-lg bg-colour3 font-bold active:scale-90 text-colour1 hover:bg-colour4 active:opacity-80 transition duration-300'
          disabled={resetRequestInProgress}
          aria-label='Request a password reset link'
        >
          {resetRequestInProgress ? (
            <div
              className='flex justify-center items-center gap-2'
              aria-live='polite'
            >
              <LoadingSpinner xs={true} />
              <span className='sr-only'>Sending reset link…</span>
            </div>
          ) : (
            <span>Send reset link</span>
          )}
        </button>
      </div>

      {/* Result live region */}
      {(resetRequestError || resetRequestSuccess) && (
        <div
          ref={alertRef}
          role='alert'
          aria-live={resetRequestError ? 'assertive' : 'polite'}
          aria-atomic='true'
          tabIndex={-1}
          className={
            resetRequestError
              ? 'text-error-red max-w-[20rem] mx-auto text-sm text-center'
              : 'text-green-600 max-w-[20rem] mx-auto text-sm text-center'
          }
        >
          {String(resetRequestError || resetRequestSuccess)}
        </div>
      )}
    </form>
  );
}

export default ForgotPasswordForm;
